import React from "react";
import { Panel } from "primereact/panel";
import { PrimeIcons } from "primereact/api";

import { TawreedCalendar, TawreedForm, TawreedFormField, TawreedFormMode, TawreedFormSubmitAction, TawreedFormSubmitCallback, TawreedFormSubmitProps, TawreedInputNumber, TawreedInputText } from "../../../../common/components/form";
import { DocumentDto, DocumentService } from "../../domain";
import { tawreedTranslationService } from "../../../../common/translations";
import { DocumentMapper, DocumentType } from "../../data";
import { PartnerAutoCompleteComponent } from "../../../partners";
import { TawreedActionName } from "../../../../common/actions";
import { FinancePaymentMethodDropdown } from "./finance-payment-method.dropdown";
import { Routes } from "../../../../router";
import { useHistory, useParams } from "react-router-dom";
import { FinanceDocumentTable } from "./finance-document-table";
import { FileService, TawreedFile } from "../../../upload";
import { GlobalContext } from "../../../../context";
import { AccountAutoCompleteComponent } from "./account-autocomplete.component";

export type FinanceDocumentFormProps = {
    type: DocumentType,
}

export const FinanceDocumentForm: React.FC<FinanceDocumentFormProps> = (props) => {
    // di
    const service: DocumentService = React.useMemo(() => new DocumentService(), []);
    const fileService: FileService = React.useMemo(() => new FileService(), []);



    const { id } = useParams<{ id: string | undefined }>();
    // states
    const [loading, setLoading] = React.useState<boolean>(false);
    const [reload, setReload] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<DocumentDto>>(DocumentMapper.initial(props.type));
    const [balance, setBalance] = React.useState<number>(0);
    const history = useHistory();
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const { constants: { constants } } = React.useContext(GlobalContext);



    const onSubmitExecute = (data: Partial<DocumentDto>, cb: TawreedFormSubmitCallback<Partial<DocumentDto>>): Promise<void> => {
        setLoading(true);
        return service.createDocument(data)
            .then((_res) => {
                if (_res) {
                    switch (props.type) {
                        case DocumentType.CREDIT_NOTE:
                            setData({...data, documentId: _res});
                            history.replace(Routes.FinanceDocumentType3Details.replace(':id', _res ? _res.toString() : "0"));
                            break;
                        case DocumentType.DEBIT_NOTE:
                            setData({...data, documentId: _res});
                            history.replace(Routes.FinanceDocumentType4Details.replace(':id', _res ? _res.toString() : "0"));
                            break;
                        case DocumentType.PAYMENT_VOUCHER:
                            setData({...data, documentId: _res});
                            history.replace(Routes.FinanceDocumentType1Details.replace(':id', _res ? _res.toString() : "0"));
                            break;
                        case DocumentType.RECEIPT_VOUCHER:
                            setData({...data, documentId: _res});
                            history.replace(Routes.FinanceDocumentType2Details.replace(':id', _res ? _res.toString() : "0"));
                            break;
                    }

                    setLoading(false);
                }
                else{
                const initial = DocumentMapper.initial(props.type);
                // setData(initial);
                setBalance(0);
                setLoading(false);
                cb(initial);
                }
            })
            .catch((err) => {
                setLoading(false);
                cb(err);
            });
    };

    React.useEffect(() => {
        let mounted = true;
        if (id) {
            if (mounted) {
                setDisabled(true);
                setLoading(true);
            }
            service.getDetails(Number.parseInt(id))
                .then(res => {

                    if (mounted) {
                        setLoading(false);
                        setData(res);
                    }
                })
                .catch(() => {
                    if (mounted) {
                        setLoading(false);
                    }
                })
            setDisabled(true);
        }
    }, [reload, id]);

    const addDocumentFile = (deliveryCost: TawreedFile, title: string) => {
        setLoading(true);
        service.createFile(deliveryCost, title, Number.parseInt(id!)).then((res) => {

        }).finally(() => {
            setReload(!reload);
        });
    }

    const deleteDocumentFile = async (id: number) => {
        try {
            setLoading(true);
            const result = await service.deleteFile(id);
            if (result) {
                let deliveryCosts = data.files?.filter(el => el.id !== id);
                setData({ ...data, files: deliveryCosts });
            }

        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    }
    const downloadFile = async (fileId: string, name?: string) => {
        try {
            setLoading(true);
            await fileService.downloadS3(fileId, name);

        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    }

    const onSubmit: TawreedFormSubmitProps<Partial<DocumentDto>> = {
        showButton: !disabled,
        resetOnSubmit: false,
        onSubmit: new TawreedFormSubmitAction<Partial<DocumentDto>>(TawreedActionName.FinanceDocumentsCreate, 'lbl_save', PrimeIcons.SAVE, onSubmitExecute),
    };

    const getBalance = (partnerId: number) => service.getBalance(partnerId).then(res => setBalance(res));

    const isVoucher = props.type === DocumentType.PAYMENT_VOUCHER || props.type === DocumentType.RECEIPT_VOUCHER;
    return (
        <TawreedForm
            className="grid form-grid"
            title={props.type < 5 ? "lbl_finance_documents_" + props.type : constants?.documentTypes.find(el => el.value === data.typeId)?.label ?? ""} loading={loading} mode="Create"
            dataKey="documentId" data={data}
            submit={onSubmit}
            useReset={!disabled}>

            <Panel header={tawreedTranslationService.translate('lbl_general_information')} className="col-12">
                <div className="grid">

                    {(!id || (id && data.partner?.partnerName)) && <TawreedFormField name="partner" title="lbl_finance_documents_partner" className="field col-12 md:col-6">
                        {<PartnerAutoCompleteComponent partnerType={undefined}
                            autoCompleteName="partner"
                            autoCompleteField="partnerName"
                            autoCompleteRules={{ required: "msg_field_is_required" }}
                            autoCompleteRender="form"
                            autoCompleteOnSelect={(e) => getBalance(e.value.partnerId)}
                            autoCompleteDisabled={disabled} />}
                    </TawreedFormField>}
                    {isVoucher &&
                    <TawreedFormField name="platformAccount" title="lbl_finance_documents_account_id" className="field col-12 md:col-6">
                    {/* <AccountDropdownComponent render="form" name="platformAccountId" rules={{ required: 'msg_field_is_required' }} disabled={disabled} /> */}
                    <AccountAutoCompleteComponent autoCompleteDisabled={disabled} accountType={14} autoCompleteName="platformAccount" autoCompleteField="accountTitle" autoCompleteRender="form" autoCompleteRules={{ required: 'msg_field_is_required' }} />
                  
                    </TawreedFormField>
                    }
                    
                    <TawreedFormField name="balance" title="lbl_finance_documents_partner_balance" className="field col-12 md:col-6">
                        <TawreedInputNumber render="standalone" name="balance" value={balance} mode="decimal" readOnly disabled />
                    </TawreedFormField>

                    <TawreedFormField name="documentDate" title="lbl_finance_documents_document_date" className="field col-12 md:col-6">
                        <TawreedCalendar render="form" name="documentDate" rules={{ required: 'msg_field_is_required' }} disabled={disabled} />
                    </TawreedFormField>

                    {
                        ((isVoucher || (id && data.paymentMethodId)) &&
                            <React.Fragment>
                                <TawreedFormField name="paymentMethodId" title="lbl_finance_documents_paymentmethodid" className="field col-12 md:col-6">
                                    <FinancePaymentMethodDropdown render="form" name="paymentMethodId" rules={{ required: 'msg_field_is_required' }} disabled={disabled} />
                                </TawreedFormField>

                            </React.Fragment>)
                    } {
                        ((isVoucher || (id)) &&
                            <React.Fragment>
                                <TawreedFormField name="paymentReference" title="lbl_finance_documents_paymentreference" className="field col-12 md:col-6">
                                    <TawreedInputText render="form" name="paymentReference" disabled={disabled} />
                                </TawreedFormField>

                            </React.Fragment>)
                    }
                    <TawreedFormField name="paymentTransactionId" title="lbl_payment_transactions_id" className="field col-12 md:col-6">
                        <TawreedInputText render="form" name="paymentTransactionId" disabled={disabled} />
                    </TawreedFormField>

                    <TawreedFormField name="amount" title="lbl_finance_documents_amount" className="field col-12 md:col-6">
                        <TawreedInputNumber render="form" name="amount" mode="decimal" rules={{ required: 'msg_field_is_required' }} disabled={disabled} />
                    </TawreedFormField>
                    {id && <TawreedFormField name="descriptionAr" title="lbl_finance_documents_description_Ar" className="field col-12">
                        <TawreedInputText render="form" name="descriptionAr" disabled={disabled} />
                    </TawreedFormField>}

                    <TawreedFormField name="description" title="lbl_finance_documents_description" className="field col-12">
                        <TawreedInputText render="form" name="description" rules={{ required: 'msg_field_is_required', maxLength: { value: 100, message: 'msg_field_is_too_long' } }} disabled={disabled} />
                    </TawreedFormField>
                </div>
            </Panel>
            {id &&
                <Panel header={tawreedTranslationService.translate('lbl_Doument_files')} className="col-12">
                    {
                        <FinanceDocumentTable
                            addDocumentFile={addDocumentFile}
                            deleteDocumentFile={deleteDocumentFile}
                            downloadDocumentFile={downloadFile}
                            data={data.files}
                            loading={loading}
                        ></FinanceDocumentTable>
                    }
                </Panel >
            }
        </TawreedForm>
    );
}
